<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Product SKU</h1>
                <div v-if="user.type === 'admin'">
                    <v-btn color="black" prepend-icon="mdi-plus" @click="duplicateFrom = null; createDialogSwitch = true">Add</v-btn>
                </div>
                <v-btn v-else color="black" prepend-icon="mdi-plus" @click="assignDialogSwitch = true">Add</v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th></th>
                        <th v-if="user.type === 'admin'">Portals</th>
                        <th v-if="user.type === 'admin'">Master Configs</th>
                        <th-btn :sortedColumn="sortedColumn" column="product_name" sortable title="Product Name"
                                @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="sku" sortable title="SKU" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="hw_revision" sortable title="HW Revision"
                                @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="manufacture_code" sortable title="Manufacture Code"
                                @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="manufacture_name" sortable title="Manufacture Name"
                                @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="product_type" sortable title="Type" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="created_at" sortable title="Created At"
                                @sort="sort"/>
                        <th v-if="user.type === 'admin'">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="sku in product_skus" v-if="!loading && product_skus.length" :key="sku.id"
                        class="text-left">
                        <td>
                            <v-avatar :image="sku.device_picture" rounded="sm"></v-avatar>
                        </td>
                        <template v-if="user.type === 'admin'">
                            <td>
                                <v-chip-group>
                                    <template v-for="(portal, index) in sku.portals" :key="index">
                                        <v-chip size="x-small">
                                            <span class="me-1">{{ portal.name }}</span>
                                        </v-chip>
                                    </template>
                                </v-chip-group>
                            </td>
                            <td>
                                <v-chip-group>
                                    <template v-for="(config, index) in sku.master_configs" :key="index">
                                        <v-chip size="x-small">
                                            <span class="me-1">{{ config.name }}</span>
                                        </v-chip>
                                    </template>
                                </v-chip-group>
                            </td>
                        </template>
                        <td v-if="user.type === 'admin'">{{ sku.product_name }}</td>
                        <td>{{ sku.sku }}</td>
                        <td>{{ sku.hw_revision }}</td>
                        <td>{{ sku.manufacture_code }}</td>
                        <td>{{ sku.manufacture_name }}</td>
                        <td class="text-capitalize">{{ type(sku.product_type) }}</td>
                        <td>{{ formatDateTime(sku.created_at) }}</td>
                        <td v-if="user.type === 'admin'">
                            <v-btn-group
                                divided
                                density="compact"
                                variant="outlined"
                            >
                                <v-btn :disabled="componentsDialogSwitch" class="text-primary" size="x-small"
                                       @click="showComponents(sku)">OTA Components
                                </v-btn>
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editProductSku(sku)">Edit
                                </v-btn>
                                <v-btn :disabled="createDialogSwitch" class="text-info" prepend-icon="mdi-content-copy"
                                       size="x-small" @click="duplicateProductSku(sku)">Duplicate
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteProductSku(sku)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="10">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="7">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchProductSkus"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <CreateSKU v-if="user.type === 'admin' && createDialogSwitch" v-model="createDialogSwitch" :duplicateFrom="duplicateFrom" @addSku="addProductSku"/>
    <EditSKU v-if="editDialogSwitch" v-model="editDialogSwitch" :sku="selectedSKU" @updateSku="updateProductSku"/>

    <Components v-if="componentsDialogSwitch" v-model="componentsDialogSwitch" :productSku="selectedSKU"/>
</template>

<script setup>
import {onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import ThBtn from "@/components/globals/th-btn.vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import EditSKU from "@/components/dialogs/devices/sku/Edit.vue";
import CreateSKU from "@/components/dialogs/devices/sku/Create.vue";
import Components from "@/components/dialogs/ota/components/Index.vue";

const product_skus = ref([]);
const {user} = useAuthStore();
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const totalDevices = ref(0);
const sortedType = ref(null);
const sortedColumn = ref(null);
const device_counts = ref([]);
const selectedSKU = ref(null);
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const duplicateFrom = ref(null);
const assignDialogSwitch = ref(false);
const componentsDialogSwitch = ref(false);


const type = (type) => {
    switch (type) {
        case 0:
            return 'Tracker';
        case 1:
            return 'Beacon';
        case 2:
            return 'Gateway';
        case 3:
            return 'Location Assistant';
        case 4:
            return 'Driver Tag';
        case 5:
            return 'Solar MPPT Controller';
    }
};


onMounted(() => {
    fetchProductSkus();
});

const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchProductSkus();
}

const fetchProductSkus = () => {
    let data = {
        portals: true,
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
    };

    loading.value = true;

    send('get', `product-skus`, data)
        .then((response) => {
            product_skus.value = response.data.data.product_skus.data;
            totalDevices.value = response.data.data.product_skus.total;
            totalPages.value = response.data.data.product_skus.last_page;
            currentPage.value = response.data.data.product_skus.current_page;
            device_counts.value = response.data.data.device_counts;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Devices could not be loaded', 'error')
        })
}

const addProductSku = (sku) => {
    product_skus.value.unshift(sku);
}

const duplicateProductSku = (sku) => {
    duplicateFrom.value = sku;
    createDialogSwitch.value = true;
}

const editProductSku = (sku) => {
    selectedSKU.value = sku;
    editDialogSwitch.value = true;
}

const showComponents = (sku) => {
    selectedSKU.value = sku;
    componentsDialogSwitch.value = true;
}

const updateProductSku = (sku) => {
    product_skus.value.map((item, index) => {
        if (item.id === sku.id) {
            product_skus.value[index] = sku;
        }
    });
}

const deleteProductSku = (sku) => {
    if (confirm(`Are you sure to delete "${sku.sku}" product sku?`)) {
        send('delete', `product-skus/${sku.id}`)
            .then((response) => {
                if (response.data.success) {
                    product_skus.value.map((dev, index) => {
                        if (dev.id === sku.id) {
                            product_skus.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`"${sku.sku}" Product SKU could not be deleted`, 'error')
            })
    }
}
</script>
